import React from 'react';
import styled, { css } from 'styled-components';
import useUser from 'hooks/use-user';
import { DutchiePaySuccess } from 'assets/dutchie-pay-success';
import SvgChevronIcon from 'assets/chevron-icon';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { EnrollmentSteps, PBB_NAME } from 'src/payments/constants';
import { CenterItems, DutchiePayHeader, DutchiePayTextBody } from 'src/payments/components';
import { useTrackEnrollmentStepViewed } from 'src/payments/hooks/use-dutchie-pay-analytics/use-track-enrollment-step-viewed';

const { alreadySignedUp } = EnrollmentSteps;

export function SuccessView(): JSX.Element {
  const { user } = useUser();
  const router = useRouter();
  const DutchiePayEnrollment = useDutchiePayEnrollment();

  const { trackEnrollmentStepSubmitted } = useDutchiePayAnalytics();

  const successFormRef = useTrackEnrollmentStepViewed({ step: EnrollmentSteps.success });

  const currentStep = useObserver(() => DutchiePayEnrollment.currentStep);
  // Look at using isDutchiePayUser but it doesn't seem to work correctly right now, will look at this in the future
  const currentStepEqualsAlreadySignedUp = currentStep === alreadySignedUp;
  const name = user?.profile?.firstName;

  const onButtonClick = (): void => {
    trackEnrollmentStepSubmitted({ step: currentStep });

    void router?.push({
      pathname: `/pay/instore`,
    });
  };

  return (
    <CenterItems ref={successFormRef}>
      <InnerContainer>
        <div>
          <IconContainer>
            <DutchiePaySuccess />
          </IconContainer>
          <DutchiePayHeader>
            {currentStepEqualsAlreadySignedUp
              ? `Welcome back, ${String(name)}!`
              : `Welcome to ${PBB_NAME} ${String(name)}!`}
          </DutchiePayHeader>
          <DutchiePayTextBody>
            {currentStepEqualsAlreadySignedUp
              ? `Good to see you again. You’ve already setup ${PBB_NAME} and are ready to go!`
              : `You are ready to use ${PBB_NAME} at any participating dispensary!`}
          </DutchiePayTextBody>
          <Button onClick={onButtonClick}>
            <ButtonContainer>
              <ButtonContents>
                <ButtonHeader>
                  {currentStepEqualsAlreadySignedUp ? `Ready to check out?` : `Are you in a dispensary?`}
                </ButtonHeader>
                <ButtonBodyText>
                  {currentStepEqualsAlreadySignedUp
                    ? `Tap right here to generate a bar code.`
                    : `Wait for your budtender to ask you to ‘Link your account’ and finishing paying with
                  ${PBB_NAME}.`}
                </ButtonBodyText>
              </ButtonContents>
              <Chevron>
                <SvgChevronIcon width='16px' />
              </Chevron>
            </ButtonContainer>
          </Button>
        </div>
        <FooterText>Not ready to check out yet? No worries, {PBB_NAME} will be here when you need it.</FooterText>
      </InnerContainer>
    </CenterItems>
  );
}

const IconContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    padding: 0 0 ${spaces[4]} 0;
  `}
`;

const Button = styled.button`
  ${({ theme: { spaces, colors } }) => css`
    background: transparent;
    border: 1px solid ${colors.grey[90]};
    border-radius: 10px;
    width: 100%;
    padding: ${spaces[6]};
    cursor: pointer;
    text-align: left;
  `}
`;

const ButtonHeader = styled.h6`
  ${({ theme: { spaces, colors } }) => css`
    color: ${colors.dutchiePayGreen};
    font-weight: 700;
    font-size: 16px;
    margin-bottom: ${spaces[1]};
  `}
`;

const FooterText = styled.p`
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[55]};
`;

const ButtonBodyText = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.grey[35]};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ButtonContents = styled.div`
  ${({ theme: { spaces } }) => css`
    padding-right: ${spaces[5]};
  `}
`;

const Chevron = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    color: ${colors.grey[45]};
    width: 10px;
    height: ${spaces[4]};
    transform: rotate(270deg);
  `}
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  height: 100%;
`;
